/**
 * Price variants from a product's option sets: one per combination of one
 * option from each set.
 *
 * On edit, a combination that already existed is returned as the WHOLE
 * existing variant, matched by its option ids. Picking fields out of it
 * instead (as this once did) silently dropped `stockCount` — which the
 * e-commerce storefronts read, a variant at "0" being hidden — together with
 * `actualPrice` and `currencyPrices`, every time a product's options were
 * edited. A combination that is new gets a fresh SKU and a zero price.
 */
import {cloneDeep} from 'lodash';

export function buildPriceVariants(
  optionSets: any[][],
  existing: any[] | undefined,
  isEdit: boolean,
  newSku: () => string,
): any[] {
  const combinations: any[][] = [];
  const backtrack = (startIndex: number, current: any[]) => {
    if (current.length === optionSets.length) {
      combinations.push(current.slice());
      return;
    }
    for (const option of optionSets[startIndex]) {
      current.push(option);
      backtrack(startIndex + 1, current);
      current.pop();
    }
  };
  backtrack(0, []);

  return combinations.map((combination) => {
    const options = combination.map((option: any) => option.id);
    const match = isEdit
      ? (existing ?? []).find((variant: any) => sameOptions(variant.options, options))
      : undefined;
    if (match) return cloneDeep(match);

    return {
      options,
      price: 0,
      optionUnit: combination.map((option: any) => option.optionName).join(' - '),
      sku: newSku(),
    };
  });
}

function sameOptions(a: any[] | undefined, b: any[]): boolean {
  if (!Array.isArray(a) || a.length !== b.length) return false;
  return a.every((id, index) => id === b[index]);
}
